<template>
  <div class="questions-screen">

    <!-- =====================================================
         HEADER
    ====================================================== -->

    <DefaultHeader
      title="Вопросы"
      subtitle="Можете задать тут любые вопросы касательно сервиса и не только"
      :offset="616"
      class="questions-header"
    />


    <!-- =====================================================
         CONTENT
    ====================================================== -->

    <main class="questions-page">

      <!-- ===================================================
           HERO
      ==================================================== -->

      <section class="questions-hero">

        <div class="hero-glow hero-glow-left"></div>
        <div class="hero-glow hero-glow-right"></div>


        <div class="hero-content">

          <div class="hero-icon">

            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6.5 4.5H17.5C19.43 4.5 21 6.07 21 8V14C21 15.93 19.43 17.5 17.5 17.5H12L7.5 20.5V17.5H6.5C4.57 17.5 3 15.93 3 14V8C3 6.07 4.57 4.5 6.5 4.5Z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

              <path
                d="M9.6 9.2C9.8 8.05 10.65 7.3 11.95 7.3C13.35 7.3 14.2 8.15 14.2 9.15C14.2 10.85 12.1 10.85 12.1 12.35"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <circle
                cx="12"
                cy="14.5"
                r="0.8"
                fill="currentColor"
              />
            </svg>

          </div>


          <div class="hero-copy">

            <span class="hero-eyebrow">
              Сообщество
            </span>

            <h1 class="hero-title">
              Вопросы и ответы
            </h1>

            <p class="hero-description">
              Делитесь опытом, обсуждайте профессиональные темы
              и находите ответы от участников сообщества.
            </p>

          </div>

        </div>


        <!-- COUNT -->

        <div class="questions-counter">

          <div class="counter-icon">

            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 6.5H17"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M7 10.5H14"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <path
                d="M7 14.5H12"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />

              <rect
                x="4"
                y="3"
                width="16"
                height="18"
                rx="4"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>

          </div>


          <div class="counter-content">

            <strong>
              {{ questions.length }}
            </strong>

            <span>
              {{ questionsCaption }}
            </span>

          </div>

        </div>

      </section>


      <!-- ===================================================
           SECTION HEADER
      ==================================================== -->

      <section class="questions-section-header">

        <div>

          <span class="section-eyebrow">
            Обсуждения
          </span>

          <h2>
            Последние вопросы
          </h2>

        </div>


        <div
          v-if="!isLoading && questions.length > 0"
          class="section-status"
        >
          <span class="status-dot"></span>

          Актуальные
        </div>

      </section>


      <!-- ===================================================
           LOADING
      ==================================================== -->

      <div
        v-if="isLoading"
        class="questions-wrapper loading-grid"
      >

        <div
          v-for="n in 6"
          :key="n"
          class="question-skeleton-wrap"
        >
          <QuestionSkeleton />
        </div>

      </div>


      <!-- ===================================================
           ERROR
      ==================================================== -->

      <section
        v-else-if="errorMessage"
        class="state-card error-state"
      >

        <div class="state-visual error">

          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="8"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="M12 8V12.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />

            <circle
              cx="12"
              cy="16"
              r="1"
              fill="currentColor"
            />
          </svg>

        </div>


        <span class="state-badge">
          Что-то пошло не так
        </span>


        <h3>
          Не удалось загрузить вопросы
        </h3>


        <p>
          {{ errorMessage }}
        </p>


        <button
          type="button"
          class="retry-button"
          @click="loadQuestions"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M19 8V4L17.5 5.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M19 5.5C17.3 3.9 15 3 12.5 3C7.8 3 4 6.8 4 11.5C4 16.2 7.8 20 12.5 20C16 20 19 17.9 20.3 14.8"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>

          Попробовать снова
        </button>

      </section>


      <!-- ===================================================
           EMPTY
      ==================================================== -->

      <section
        v-else-if="questions.length === 0"
        class="state-card empty-state"
      >

        <div class="empty-orbit">

          <div class="empty-orbit-ring"></div>

          <div class="state-visual">

            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6.5 4.5H17.5C19.43 4.5 21 6.07 21 8V14C21 15.93 19.43 17.5 17.5 17.5H12L7.5 20.5V17.5H6.5C4.57 17.5 3 15.93 3 14V8C3 6.07 4.57 4.5 6.5 4.5Z"
                stroke="currentColor"
                stroke-width="1.55"
              />

              <path
                d="M8 9H16"
                stroke="currentColor"
                stroke-width="1.55"
                stroke-linecap="round"
              />

              <path
                d="M8 13H13"
                stroke="currentColor"
                stroke-width="1.55"
                stroke-linecap="round"
              />
            </svg>

          </div>

        </div>


        <span class="state-badge">
          Начните обсуждение
        </span>


        <h3>
          Здесь пока нет вопросов
        </h3>


        <p>
          Когда участники сообщества начнут задавать вопросы,
          они появятся на этой странице.
        </p>

      </section>


      <!-- ===================================================
           QUESTIONS
      ==================================================== -->

      <div
        v-else
        class="questions-wrapper"
      >

        <div
          v-for="(question, index) in questions"
          :key="question.id"
          class="question-card-wrapper"
          :style="{
            '--delay':
              `${Math.min(index, 8) * 45}ms`
          }"
        >

          <QuestionCard
            :question="question"
            :colorIndex="index % 5"
            @click="selectedQuestion = question"
          />

        </div>

      </div>

    </main>


    <!-- =====================================================
         FOOTER
    ====================================================== -->

    <MiniFooter
      class="fixed-footer"
    />


    <!-- =====================================================
         DRAWER
    ====================================================== -->

    <QuestionDrawer
      v-if="selectedQuestion"
      :question="selectedQuestion"
      @close="selectedQuestion = null"
    />

  </div>
</template>


<script setup>

import {
  computed,
  onMounted,
  ref
} from "vue";

import {
  waitForAuthReady
} from "../utils/auth.js";

import QuestionService
  from "../api/QuestionService";

import DefaultHeader
  from "../components/QuestionsHeader.vue";

import QuestionCard
  from "../components/QuestionCard.vue";

import MiniFooter
  from "../components/MiniFooter.vue";

import QuestionSkeleton
  from "../components/QuestionSkeleton.vue";

import QuestionDrawer
  from "../views/QuestionDrawer.vue";


/* ============================================================
   STATE
============================================================ */

const questions =
  ref([]);

const isLoading =
  ref(true);

const errorMessage =
  ref("");

const selectedQuestion =
  ref(null);


/* ============================================================
   COUNTER TITLE
============================================================ */

const questionsCaption =
  computed(() => {

    const count =
      questions.value.length;

    const lastTwo =
      count % 100;

    const lastOne =
      count % 10;


    if (
      lastTwo >= 11
      && lastTwo <= 19
    ) {
      return "вопросов";
    }


    if (
      lastOne === 1
    ) {
      return "вопрос";
    }


    if (
      lastOne >= 2
      && lastOne <= 4
    ) {
      return "вопроса";
    }


    return "вопросов";
  });


/* ============================================================
   LOAD QUESTIONS
============================================================ */

const loadQuestions =
  async () => {

    if (
      isLoading.value
      && questions.value.length > 0
    ) {
      return;
    }


    isLoading.value =
      true;

    errorMessage.value =
      "";


    try {

      questions.value =
        await QuestionService
          .fetchQuestions();

    } catch (error) {

      console.error(
        "❌ Ошибка загрузки вопросов:",
        error
      );


      errorMessage.value =
        error?.response?.data?.error
        || error?.message
        || "Не удалось получить список вопросов";

    } finally {

      isLoading.value =
        false;

    }

  };


/* ============================================================
   AUTH + INITIAL LOAD
============================================================ */

onMounted(
  async () => {

    try {

      /*
       * Ждём восстановления Firebase-сессии.
       */
      const user =
        await waitForAuthReady();


      /*
       * Пользователь не авторизован.
       */
      if (!user) {

        console.warn(
          "❌ Questions: пользователь не авторизован"
        );


        window.location.replace(
          "https://hr3.space/"
        );

        return;
      }


      /*
       * Только после авторизации
       * загружаем вопросы.
       */
      await loadQuestions();

    } catch (error) {

      console.error(
        "❌ Ошибка проверки авторизации Questions:",
        error
      );


      window.location.replace(
        "https://hr3.space/"
      );

    }

  }
);

</script>


<style scoped>

/* ============================================================
   PAGE
============================================================ */

.questions-screen {
  min-height: 100dvh;

  display: flex;
  flex-direction: column;

  color: #242a33;

  background:
    radial-gradient(
      circle at 9% 8%,
      rgba(75, 137, 229, 0.10),
      transparent 27%
    ),
    radial-gradient(
      circle at 92% 18%,
      rgba(117, 91, 216, 0.07),
      transparent 28%
    ),
    radial-gradient(
      circle at 50% 90%,
      rgba(81, 149, 219, 0.055),
      transparent 32%
    ),
    #f5f7fa;
}


.questions-page {
  width: 100%;
  max-width: 1080px;

  flex: 1;

  margin: 0 auto;

  box-sizing: border-box;

  padding:
    36px
    24px
    110px;
}


/* ============================================================
   HERO
============================================================ */

.questions-hero {
  position: relative;

  min-height: 175px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 34px;

  box-sizing: border-box;

  margin-bottom: 34px;

  padding:
    28px
    30px;

  overflow: hidden;

  background:
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.97),
      rgba(249, 251, 255, 0.93)
    );

  border:
    1px solid
    rgba(37, 65, 105, 0.075);

  border-radius: 26px;

  box-shadow:
    0 18px 50px
    rgba(27, 47, 80, 0.065);

  backdrop-filter:
    blur(20px);

  -webkit-backdrop-filter:
    blur(20px);
}


.hero-glow {
  position: absolute;

  pointer-events: none;

  border-radius: 50%;

  filter:
    blur(4px);
}


.hero-glow-left {
  top: -150px;
  left: -90px;

  width: 330px;
  height: 330px;

  background:
    radial-gradient(
      circle,
      rgba(59, 130, 224, 0.15),
      transparent 67%
    );
}


.hero-glow-right {
  top: -180px;
  right: -80px;

  width: 350px;
  height: 350px;

  background:
    radial-gradient(
      circle,
      rgba(121, 92, 215, 0.11),
      transparent 67%
    );
}


.hero-content {
  position: relative;

  z-index: 2;

  min-width: 0;

  display: flex;
  align-items: center;

  gap: 20px;
}


.hero-icon {
  width: 64px;
  height: 64px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #527fca;

  background:
    linear-gradient(
      145deg,
      rgba(78, 139, 231, 0.11),
      rgba(109, 88, 208, 0.075)
    );

  border:
    1px solid
    rgba(74, 123, 202, 0.09);

  border-radius: 20px;

  box-shadow:
    0 9px 24px
    rgba(66, 111, 180, 0.08);
}


.hero-icon svg {
  width: 31px;
  height: 31px;
}


.hero-copy {
  min-width: 0;
}


.hero-eyebrow {
  display: block;

  margin-bottom: 6px;

  color: #8290a4;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  letter-spacing: 0.09em;

  text-transform: uppercase;
}


.hero-title {
  margin: 0;

  color: #252b33;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 25px;

  line-height: 1.15;

  letter-spacing: -0.45px;
}


.hero-description {
  max-width: 535px;

  margin:
    8px
    0
    0;

  color: #858d98;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;

  line-height: 1.6;
}


/* ============================================================
   COUNTER
============================================================ */

.questions-counter {
  position: relative;

  z-index: 2;

  min-width: 138px;

  display: flex;
  align-items: center;

  gap: 11px;

  box-sizing: border-box;

  padding:
    13px
    15px;

  flex-shrink: 0;

  background:
    rgba(244, 248, 255, 0.82);

  border:
    1px solid
    rgba(59, 105, 175, 0.07);

  border-radius: 17px;
}


.counter-icon {
  width: 34px;
  height: 34px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #5b83c4;

  background:
    rgba(72, 128, 213, 0.08);

  border-radius: 11px;
}


.counter-icon svg {
  width: 17px;
  height: 17px;
}


.counter-content {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 2px;
}


.counter-content strong {
  color: #3d68a9;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;

  line-height: 1;
}


.counter-content span {
  color: #8792a1;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   SECTION HEADER
============================================================ */

.questions-section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 20px;

  margin-bottom: 17px;

  padding:
    0
    2px;
}


.section-eyebrow {
  display: block;

  margin-bottom: 4px;

  color: #9aa1ab;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  letter-spacing: 0.08em;

  text-transform: uppercase;
}


.questions-section-header h2 {
  margin: 0;

  color: #313740;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 17px;

  letter-spacing: -0.15px;
}


.section-status {
  display: flex;
  align-items: center;

  gap: 6px;

  padding:
    7px
    10px;

  color: #768391;

  background:
    rgba(255, 255, 255, 0.67);

  border:
    1px solid
    rgba(30, 50, 80, 0.055);

  border-radius: 999px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


.status-dot {
  width: 6px;
  height: 6px;

  background: #45b979;

  border-radius: 50%;

  box-shadow:
    0 0 0 3px
    rgba(69, 185, 121, 0.09);
}


/* ============================================================
   GRID
============================================================ */

.questions-wrapper {
  width: 100%;

  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap: 16px;
}


/* ============================================================
   QUESTION WRAPPER
============================================================ */

.question-card-wrapper {
  min-width: 0;

  animation:
    questionAppear
    0.42s
    cubic-bezier(
      0.22,
      1,
      0.36,
      1
    )
    both;

  animation-delay:
    var(--delay);
}


@keyframes questionAppear {

  from {
    opacity: 0;

    transform:
      translateY(11px)
      scale(0.992);
  }

  to {
    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }

}


/* ============================================================
   CHILD QUESTION CARD
   scoped -> :deep()
============================================================ */

.question-card-wrapper
:deep(.question-card) {
  height: 100%;

  box-sizing: border-box;

  margin: 0;

  padding: 19px;

  overflow: hidden;

  background:
    rgba(255, 255, 255, 0.93);

  border:
    1px solid
    rgba(32, 50, 75, 0.07);

  border-radius: 20px;

  box-shadow:
    0 9px 28px
    rgba(20, 35, 60, 0.045);

  cursor: pointer;

  transition:
    transform 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease;
}


.question-card-wrapper
:deep(.question-card:hover) {
  transform:
    translateY(-3px);

  border-color:
    rgba(70, 126, 209, 0.17);

  box-shadow:
    0 17px 38px
    rgba(35, 66, 110, 0.095);
}


.question-card-wrapper
:deep(.question-title) {
  color: #303740;

  font-size: 16px;

  line-height: 1.35;
}


.question-card-wrapper
:deep(.question-description) {
  color: #7f8792;

  font-size: 11px;

  line-height: 1.55;
}


.question-card-wrapper
:deep(.question-stats) {
  color: #9199a5;

  font-size: 9px;
}


/* ============================================================
   STATES
============================================================ */

.state-card {
  min-height: 315px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    42px
    24px;

  text-align: center;

  background:
    rgba(255, 255, 255, 0.78);

  border:
    1px solid
    rgba(30, 50, 80, 0.065);

  border-radius: 24px;

  box-shadow:
    0 15px 42px
    rgba(20, 40, 70, 0.045);
}


.state-visual {
  position: relative;

  z-index: 2;

  width: 72px;
  height: 72px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 17px;

  color: #5d83c4;

  background:
    linear-gradient(
      145deg,
      #f4f8ff,
      #f3f2fb
    );

  border:
    1px solid
    rgba(71, 111, 181, 0.08);

  border-radius: 22px;

  box-shadow:
    0 10px 27px
    rgba(48, 88, 150, 0.07);
}


.state-visual svg {
  width: 32px;
  height: 32px;
}


.state-visual.error {
  color: #cf6868;

  background:
    rgba(220, 89, 89, 0.07);
}


.empty-orbit {
  position: relative;

  width: 116px;
  height: 102px;

  display: flex;
  align-items: center;
  justify-content: center;
}


.empty-orbit-ring {
  position: absolute;

  width: 108px;
  height: 108px;

  background:
    radial-gradient(
      circle,
      rgba(72, 132, 218, 0.11),
      transparent 67%
    );

  filter:
    blur(3px);
}


.empty-orbit
.state-visual {
  margin: 0;
}


.state-badge {
  margin-bottom: 10px;

  padding:
    6px
    10px;

  color: #7588a6;

  background:
    rgba(69, 117, 187, 0.065);

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing: 0.035em;
}


.state-card h3 {
  margin: 0;

  color: #3c424b;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 16px;
}


.state-card p {
  max-width: 390px;

  margin:
    8px
    0
    0;

  color: #939ba5;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.6;
}


/* ============================================================
   RETRY
============================================================ */

.retry-button {
  height: 38px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  margin-top: 19px;

  padding:
    0
    14px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #4b83d7,
      #6673dc
    );

  border: 0;

  border-radius: 11px;

  cursor: pointer;

  box-shadow:
    0 7px 17px
    rgba(65, 104, 190, 0.18);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}


.retry-button:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 10px 22px
    rgba(65, 104, 190, 0.24);
}


.retry-button svg {
  width: 15px;
  height: 15px;
}


/* ============================================================
   FOOTER
============================================================ */

.fixed-footer {
  flex-shrink: 0;

  width: 100%;

  z-index: 100;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.questions-screen {
  color: #f0f1f4;

  background:
    radial-gradient(
      circle at 9% 8%,
      rgba(61, 114, 196, 0.11),
      transparent 27%
    ),
    radial-gradient(
      circle at 92% 18%,
      rgba(108, 82, 194, 0.085),
      transparent 28%
    ),
    radial-gradient(
      circle at 50% 90%,
      rgba(58, 115, 177, 0.06),
      transparent 32%
    ),
    #181a1e;
}


html[data-theme="dark"]
.questions-hero {
  background:
    linear-gradient(
      135deg,
      rgba(35, 38, 44, 0.97),
      rgba(30, 32, 38, 0.95)
    );

  border-color:
    rgba(255, 255, 255, 0.06);

  box-shadow:
    0 18px 50px
    rgba(0, 0, 0, 0.19);
}


html[data-theme="dark"]
.hero-title {
  color: #f0f2f5;
}


html[data-theme="dark"]
.hero-description {
  color: #898f99;
}


html[data-theme="dark"]
.hero-eyebrow {
  color: #7f8998;
}


html[data-theme="dark"]
.hero-icon {
  color: #96baf1;

  background:
    rgba(70, 120, 205, 0.10);

  border-color:
    rgba(120, 160, 220, 0.08);
}


html[data-theme="dark"]
.questions-counter {
  background:
    rgba(255, 255, 255, 0.035);

  border-color:
    rgba(255, 255, 255, 0.055);
}


html[data-theme="dark"]
.counter-icon {
  color: #91b2e7;

  background:
    rgba(80, 130, 205, 0.09);
}


html[data-theme="dark"]
.counter-content strong {
  color: #9fc1f4;
}


html[data-theme="dark"]
.counter-content span {
  color: #78808c;
}


html[data-theme="dark"]
.questions-section-header h2 {
  color: #e3e5e9;
}


html[data-theme="dark"]
.section-eyebrow {
  color: #6f7680;
}


html[data-theme="dark"]
.section-status {
  color: #898f98;

  background:
    rgba(255, 255, 255, 0.03);

  border-color:
    rgba(255, 255, 255, 0.05);
}


html[data-theme="dark"]
.question-card-wrapper
:deep(.question-card) {
  background:
    rgba(35, 37, 42, 0.96);

  border-color:
    rgba(255, 255, 255, 0.06);

  box-shadow:
    0 10px 30px
    rgba(0, 0, 0, 0.16);
}


html[data-theme="dark"]
.question-card-wrapper
:deep(.question-card:hover) {
  border-color:
    rgba(91, 142, 219, 0.18);

  box-shadow:
    0 17px 40px
    rgba(0, 0, 0, 0.28);
}


html[data-theme="dark"]
.question-card-wrapper
:deep(.question-title) {
  color: #ededf0;
}


html[data-theme="dark"]
.question-card-wrapper
:deep(.question-description) {
  color: #959ca6;
}


html[data-theme="dark"]
.question-card-wrapper
:deep(.question-stats) {
  color: #777f8a;
}


html[data-theme="dark"]
.state-card {
  background:
    rgba(35, 37, 42, 0.88);

  border-color:
    rgba(255, 255, 255, 0.06);

  box-shadow:
    0 15px 42px
    rgba(0, 0, 0, 0.18);
}


html[data-theme="dark"]
.state-visual {
  color: #9ebef0;

  background:
    rgba(75, 120, 195, 0.09);

  border-color:
    rgba(130, 165, 220, 0.07);
}


html[data-theme="dark"]
.state-visual.error {
  color: #e58e8e;

  background:
    rgba(220, 89, 89, 0.08);
}


html[data-theme="dark"]
.state-badge {
  color: #91a9cc;

  background:
    rgba(80, 120, 185, 0.08);
}


html[data-theme="dark"]
.state-card h3 {
  color: #e0e2e6;
}


html[data-theme="dark"]
.state-card p {
  color: #7f8690;
}


/* ============================================================
   TABLET
============================================================ */

@media (max-width: 850px) {

  .questions-page {
    padding:
      28px
      18px
      100px;
  }


  .questions-hero {
    padding:
      24px
      22px;
  }


  .questions-wrapper {
    grid-template-columns:
      1fr;
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 600px) {

  .questions-page {
    padding:
      16px
      12px
      92px;
  }


  .questions-hero {
    min-height: 0;

    flex-direction: column;
    align-items: stretch;

    gap: 20px;

    margin-bottom: 25px;

    padding:
      20px
      18px;

    border-radius: 21px;
  }


  .hero-content {
    align-items: flex-start;

    gap: 14px;
  }


  .hero-icon {
    width: 50px;
    height: 50px;

    border-radius: 16px;
  }


  .hero-icon svg {
    width: 25px;
    height: 25px;
  }


  .hero-title {
    font-size: 20px;
  }


  .hero-description {
    font-size: 10px;

    line-height: 1.55;
  }


  .questions-counter {
    width: 100%;

    min-width: 0;

    padding:
      11px
      13px;
  }


  .questions-section-header {
    margin-bottom: 14px;
  }


  .questions-section-header h2 {
    font-size: 15px;
  }


  .questions-wrapper {
    gap: 12px;
  }


  .question-card-wrapper
  :deep(.question-card) {
    padding: 16px;

    border-radius: 18px;
  }


  .state-card {
    min-height:
      calc(
        100dvh - 440px
      );

    padding:
      36px
      18px;

    border-radius: 21px;
  }


  .fixed-footer {
    position: fixed;

    left: 0;
    right: 0;
    bottom: 0;

    z-index: 100;
  }

}


/* ============================================================
   SMALL MOBILE
============================================================ */

@media (max-width: 390px) {

  .questions-page {
    padding-left: 10px;
    padding-right: 10px;
  }


  .hero-description {
    max-width: 245px;
  }


  .section-status {
    display: none;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion:
  reduce
) {

  .question-card-wrapper,
  .question-card-wrapper
  :deep(.question-card),
  .retry-button {
    animation: none;
    transition: none;
  }

}

</style>